<template>
  <div>
    <!-- 抽奖转盘 -->
    <div :class="$style.wheel" :style="{ backgroundImage: `url('${prizeImg}')` }">
      <div
        :class="$style['wheel-arrow']"
        :style="{
          transform: `rotateZ(${rotationDegree}deg)`,
          transitionDuration: `${animationDuration}ms`
        }"
      />
      <button
        :disabled="isPlaying || completeLottery"
        @click="onSpin"
        :class="$style['wheel-start']"
      />
    </div>

    <!-- 非实物中奖提示 -->
    <popup-frame ref="result" @close="isPlaying = false">
      <div :class="$style.result">
        <p style="margin-bottom: 0.5rem">
          恭喜您抽中
          <em>【{{ currentPrizeName }}】</em>
          ，奖励已发放至游戏内邮箱中，请团长登录游戏查收哦~
        </p>
        <button :class="$style['btn-ok']" @click="$refs.result.hide()" />
      </div>
    </popup-frame>

    <!-- 实物中奖提示 -->
    <popup-frame ref="result-goods" @close="isPlaying = false">
      <div :class="$style.result">
        <p style="margin-bottom: 0.5rem">
          恭喜您抽中
          <em>【{{ currentPrizeName }}】</em>
          ，请填写您的收货地址，雪儿将在活动结束后15个工作日内统一寄出~
        </p>
        <button :class="$style['btn-address']" @click="onBindAddress()" />
      </div>
    </popup-frame>

    <!-- 无抽奖次数 -->
    <popup-frame ref="result-no-lottery" size="mini">
      <p
        v-html="noLotteryTip"
        style="
          padding: 0 0.5rem;
          font-size: 0.32rem;
          font-family: 'Source Han Serif SC Bold';
          line-height: 1.8;
        "
      />
    </popup-frame>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { lottery } from '@/api'
import { PopupFrame } from '@/components/widgets'

export default {
  name: 'TheLotteryWheel',
  components: { PopupFrame },
  props: {
    /** 抽奖配置数据 */
    dataSource: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      /** 抽奖动画是否进行中 */
      isPlaying: false,
      /** 箭头旋转角度(度数) */
      rotationDegree: 0,
      /** 动画过渡时间(毫秒) */
      animationDuration: 50,
      /** 当前抽中的奖品ID */
      currentPrizeId: 0,
      /** 当前奖品名称 */
      currentPrizeName: ''
    }
  },
  computed: {
    ...mapGetters(['lotteryCount', 'isBindRole', 'completeLottery']),
    /** 抽中的奖品(在转盘上的位置，从0开始，顺时针数) */
    prizesMap() {
      const { awards_map } = this.dataSource || {}
      return awards_map ? JSON.parse(awards_map) : {}
    },
    /** 转盘图片 */
    prizeImg() {
      return this.dataSource.turntable_img || ''
    },
    /** 奖品数 */
    prizeCount() {
      return this.dataSource.prize_count || 10
    },
    /** 每一个奖品对应的旋转角度 */
    degreePerPrize() {
      return 360 / this.prizeCount
    },
    /** 指针偏移角度 */
    offsetDegree() {
      return this.dataSource.arrow_offset_degree || 0
    },
    /** 实物奖品表 */
    goodsList() {
      if (this.dataSource.tangible_prize_id) {
        return this.dataSource.tangible_prize_id.split(',').map(Number)
      }
      return []
    },
    /** 无抽奖次数提示文本 */
    noLotteryTip() {
      return this.dataSource.no_lottery || '无抽奖次数'
    }
  },
  methods: {
    ...mapActions(['setLotteryCount', 'setDrawnLotteryCount']),
    async onSpin() {
      if (!this.isBindRole) {
        this.$emit('login')
        return
      }

      // 无抽奖次数
      if (this.lotteryCount <= 0) {
        this.$refs['result-no-lottery'].show()
        return
      }

      try {
        const { data } = await lottery()
        if (data) {
          // 旋转开始
          this.isPlaying = true
          this.currentPrizeId = data.id
          this.currentPrizeName = data.award_name
          this.startRotationAnimation()

          // 更新抽奖次数
          this.setLotteryCount(data.residue)
          this.setDrawnLotteryCount(data.draw_total)
        } else {
          this.isPlaying = false
        }
      } catch {
        this.isPlaying = false
      }
    },
    /**
     * 执行转盘旋转动画
     * 使用requestAnimationFrame实现平滑动画效果
     */
    startRotationAnimation() {
      const arrowElement = this.$el.querySelector(`.${this.$style['wheel-arrow']}`)
      if (!arrowElement) {
        this.isPlaying = false
        return
      }

      const animationStartTime = Date.now()
      const totalAnimationDuration = 3000 // 动画总时长3秒
      const initialDegree = this.rotationDegree % 360 // 确保起始角度在0-360度范围内
      const targetRotationDegree =
        Number(this.prizesMap[this.currentPrizeId]) * this.degreePerPrize +
        Number(this.offsetDegree)
      const rotationCount = 4 // 固定旋转4圈

      // 计算总旋转角度 = 固定圈数 + (目标角度 - 起始角度)
      const totalRotationDegrees = 360 * rotationCount + (targetRotationDegree - initialDegree)

      const animateRotation = () => {
        const elapsedTime = Date.now() - animationStartTime
        const animationProgress = Math.min(elapsedTime / totalAnimationDuration, 1) // 限制在0-1范围内

        // 使用三次方缓动函数实现平滑的加速减速效果
        const getEaseProgress = progress =>
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2

        // 更新当前角度
        this.rotationDegree =
          initialDegree + totalRotationDegrees * getEaseProgress(animationProgress)

        if (animationProgress < 1) {
          // 动画继续
          requestAnimationFrame(animateRotation)
        } else {
          // 动画结束处理
          this.handleAnimationEnd(arrowElement, targetRotationDegree)
        }
      }

      animateRotation()
    },

    /**
     * 处理动画结束逻辑
     * @param {HTMLElement} arrowElement - 箭头DOM元素
     * @param {number} targetRotationDegree - 目标角度
     */
    handleAnimationEnd(arrowElement, targetRotationDegree) {
      // 临时禁用transition确保精确设置最终角度
      arrowElement.style.transition = 'none'
      this.rotationDegree = targetRotationDegree % 360

      // 强制重绘确保样式应用
      void arrowElement.offsetWidth

      // 显示中奖弹窗
      if (this.goodsList.includes(this.currentPrizeId)) {
        this.$refs['result-goods'].show()
      } else {
        this.$refs.result.show()
      }
    },
    onBindAddress() {
      this.$refs['result-goods'].hide()
      this.$emit('bind-address', this.currentPrizeName)
    }
  }
}
</script>

<style lang="less" module>
.wheel {
  .size(6.5rem);

  position: relative;
  background-size: 100% 100%;
  margin: 0 auto;

  &-arrow {
    .size(2.01rem, 2.58rem);
    .background-fill('./images/wheel-arrow.png');

    position: absolute;
    top: 1.62rem;
    left: 2.23rem;

    transform-origin: 1rem 1.58rem;
    transition-property: transform;
    transition-timing-function: cubic-bezier(0.1, 0.7, 0.1, 1);
  }

  &-start {
    .size(1.9rem);
    .background-fill('./images/wheel-start.png', 1.37rem 1.4rem, center);

    position: absolute;
    top: 2.24rem;
    left: 2.3rem;

    &::after {
      .size(0.32rem);
      .background-fill('./images/red-dot.png');

      content: '';
      position: absolute;
      top: 0.2rem;
      right: 0.2rem;
    }

    &:disabled {
      opacity: 0.5;

      &::after {
        opacity: 0;
      }
    }
  }
}

.result {
  padding-top: 1rem;
  font-size: 0.3rem;
  line-height: 1.8;
  text-align: center;

  &::before {
    .size(6.9rem, 2.09rem);
    .background-fill('./images/title.png');

    content: '';
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
}

.btn-ok {
  .size(1.95rem, 0.66rem);
  .background-fill('./images/btn-ok.png');
}

.btn-address {
  .size(2.35rem, 0.66rem);
  .background-fill('./images/btn-address.png');
}
</style>
